/**
 * The Fly Agent splash (owner, 29 Sep 26: "use one of these logos as intro loading screen
 * whenever refreshed or first loaded"): the paper plane and the wordmark, centred on the
 * page's own colour. It is the funnel's loading screen (FunnelLoader) and the intro veil
 * (IntroVeil), drawn the same way so one hands over to the other without a jump. Inline
 * styles only, because the loader shows before any stylesheet of the app has loaded.
 *
 * Two things made it jump on a refresh (30 Sep 26: "the logo and title isnt center aligned and it
 * lags"): the words were live Manrope text, which only loads with the app's styles, so they showed
 * in the fallback font first and the row re-centred when Manrope arrived; and the loading screen
 * was 100vh tall while the veil is the visible screen, which on a phone with the browser's bars
 * are not the same height, so the logo moved up when the veil took over. The words are outlines
 * now (wordmarkPaths.ts) and every draw is fixed to the visible screen.
 */
import { useState, type CSSProperties, type Ref } from 'react';
import { planeSvg } from './FlyPlane';
import { WORDMARK } from './wordmarkPaths';

type Theme = 'light' | 'dark';

// The splash is drawn several times while a page loads (the loading screen, the sign-in
// wait, the page's own code, then the intro veil). It must look like ONE still screen: the
// logo fades in only the very first time in a page load and never moves until the plane
// takes off (owner, 29 Sep 26: "the intro blinks 2 times ... make it smoother").
let firstSplash = true;
const KEYFRAMES = `
@keyframes agsplash-in { from { opacity: 0; } to { opacity: 1; } }
@media (prefers-reduced-motion: no-preference) {
  .agsplash-in { animation: agsplash-in 360ms ease-out both; }
}`;

export function BrandSplash({
  theme,
  planeRef,
  rowRef,
  leaving = false,
  hidePlane = false,
}: {
  theme: Theme;
  planeRef?: Ref<HTMLSpanElement>;
  /** The logo row (plane + words): the flight loops around it. */
  rowRef?: Ref<HTMLDivElement>;
  /** Kept for the callers; every draw is fixed to the visible screen now. */
  fixed?: boolean;
  leaving?: boolean;
  hidePlane?: boolean;
}) {
  const dark = theme === 'dark';
  const wrap: CSSProperties = {
    position: 'fixed',
    inset: 0,
    zIndex: 2147482000,
    display: 'grid',
    placeItems: 'center',
    background: dark ? '#131827' : '#ffffff',
    colorScheme: dark ? 'dark' : 'light',
    opacity: leaving ? 0 : 1,
    transition: 'opacity 460ms ease-out',
    pointerEvents: leaving ? 'none' : 'auto',
  };
  const size = typeof window !== 'undefined' && window.innerWidth < 480 ? 60 : 76;
  // The words at the size the text had (half the plane), from the outlines' own units.
  const em = Math.round(size * 0.5);
  const wordsH = (WORDMARK.height * em) / WORDMARK.upm;
  const wordsW = (WORDMARK.width * em) / WORDMARK.upm;
  // Only the first draw of the page load fades in; every later draw is already there.
  const [fadeIn] = useState(() => {
    const first = firstSplash;
    firstSplash = false;
    return first;
  });
  return (
    <div style={wrap} aria-hidden="true">
      <style>{KEYFRAMES}</style>
      <div ref={rowRef} className={fadeIn ? 'agsplash-in' : undefined} style={{ display: 'flex', alignItems: 'center', gap: size * 0.28 }}>
        <span
          ref={planeRef}
          style={{ display: 'inline-block', lineHeight: 0, opacity: hidePlane ? 0 : 1, filter: 'drop-shadow(0 6px 14px rgba(102, 54, 240, 0.28))' }}
          dangerouslySetInnerHTML={{ __html: planeSvg(size, 'splash') }}
        />
        <svg width={Math.round(wordsW)} height={Math.round(wordsH)} viewBox={WORDMARK.viewBox} role="img" aria-label="Fly Agent" style={{ display: 'block', flex: 'none' }}>
          <path d={WORDMARK.fly} fill={dark ? '#f2f4f9' : '#121f2d'} />
          <path d={WORDMARK.agent} fill={dark ? '#a78bfa' : '#6636f0'} />
        </svg>
      </div>
    </div>
  );
}
